import { memo, useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import * as Utility from '../../../shared/Utility'
import { cloneDeep } from 'lodash'
import { getBalanceRulesByRelationshipType, getDisabledFields } from '../BalanceRules'
import * as Constants from '../../../../config/Constants'
import * as actions from '../../../../actions'
import getText from '../../../shared/i18n/labels'
import { StarTable } from '../../../shared/tables/StarTable'

// MODAL
import { YearlyBalanceExportModal } from './NewYearlyBalanceExportModal'
import { YearlyBalanceImportModal } from './NewYearlyBalanceImportModal'
import { CreateCommunicationModal } from '../communications/NewCreateCommunicationModal'

// FORM
import { BwmSelect } from '../../../shared/form/BwmSelect'
import { BwmInput } from '../../../shared/form/BwmInput'

import NumberFormat from 'react-number-format'

// COMPONENTS
import Dropdown from 'react-bootstrap/Dropdown'
import { DropdownButton } from 'react-bootstrap'

import { baseSearchObject, fieldTypes, TableUtils } from '../../../shared/tables/TableUtils'
import { PopupError } from '../../../shared/popups/PopupError'
import { StarTooltip } from '../../../shared/tooltips/Tooltip'
import { BtnFilter } from '../../../shared/BtnFilter'

const tableName = 'cora-relationships-yearly-balances'

const getTableContainerWidth = () => {
  if (window.innerWidth < Constants.BREAKPOINT_MD) return '150%'
  if (window.innerWidth <= Constants.BREAKPOINT_LG) return '120%'
  return '100%'
}

//memoizzo la cella per impedire rerender non necessari. Potrebbe valere la pena memoizzare l'intera riga
const NumberCell = memo(function NumberCell({
  value,
  valueName,
  relationship,
  className,
  disabled,
  rules,
  handler
}) {
  return (
    <StarTooltip text={rules?.[valueName]}>
      <div className={`${className} number-format-label`}>
        <NumberFormat
          key={`${valueName}_${relationship.id}`}
          name={`${valueName}_${relationship.id}`}
          thousandSeparator={'.'}
          decimalSeparator={','}
          decimalScale={0}
          fixedDecimalScale={true}
          className="form-control form-control-cell"
          inputMode="numeric"
          value={disabled ? '-' : value}
          readOnly={disabled}
          disabled={disabled}
          onValueChange={(e) => handler(relationship, valueName, e)}
        />
      </div>
    </StarTooltip>
  )
})

export function YearlyBalanceList(props) {
  const dispatch = useDispatch()

  const lang = useSelector((state) => state.language.language)
  const labels = getText(lang)

  const [checkedItems, setCheckedItems] = useState([])
  const [dropdownYears, setDropdownYears] = useState([])
  const [selectedYear, setSelectedYear] = useState(
    new Date().getMonth() > 4 ? new Date().getFullYear() : new Date().getFullYear() - 1
  )
  const [excludedItems, setExcludedItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [hideSearch, setHideSearch] = useState(true)
  const [rows, setRows] = useState([])

  const subject = useSelector((state) => state.subject.data)
  const [yearlyBalanceRelationships, setYearlyBalanceRelationships] = useState([])
  const currencies = useSelector((state) => state.currencies.data)
  const currencyTypes = useSelector((state) => state.currencyTypes.data)
  const [isEdit, setIsEdit] = useState(false)

  const [searchObject, setSearchObject] = useState({
    ...baseSearchObject,
    page: 0,
    year: new Date().getMonth() > 4 ? new Date().getFullYear() : new Date().getFullYear() - 1,
    parentId: props.subjectId
  })

  useEffect(() => {
    _setDropdownYears()
    getAllCurrency()
  }, [])

  useEffect(() => {
    setRows(prepareRows(yearlyBalanceRelationships))
  }, [yearlyBalanceRelationships])

  const getYearlyBalance = async (
    subjectId,
    year,
    _searchObject,
    isExternal = false,
    alsoSave = true
  ) => {
    if (alsoSave) await saveBalances(false)
    setLoading(true)
    return actions
      .searchCommunicationsBalanceBySubjectIdAndYear(subjectId, year, _searchObject)
      .then((res) => {
        if (isExternal) setYearlyBalanceRelationships(res.content)
        setLoading(false)
        return res
      })
  }

  const setNewCheckedItems = (list = []) => {
    setCheckedItems(list)
  }

  const getAllCurrency = () => {
    if (!currencies?.length) {
      dispatch(actions.getAllCurrency())
    }
    if (!currencyTypes?.length) {
      dispatch(actions.getAllCurrencyTypes())
    }
  }

  const openGuideBalances = () => {
    window.open('https://www.starsoluzioni.it/media/TABELLA-RAPPORTI.pdf')
    Utility.notifySuccess(labels.FILE_DOWNLOADED)
  }

  const saveBalances = (reloadList = true) => {
    if (!isEdit) {
      return Promise.resolve()
    }
    const yearlyBalances = {
      subjectId: parseInt(props.subjectId),
      year: String(selectedYear),
      relationships: yearlyBalanceRelationships
    }

    return actions
      .updateYearlyBalances(yearlyBalances)
      .then(() => {
        setIsEdit(false)
        Utility.notifySuccess(labels.DATA_SAVED_SUCCESSFULLY)
        if (reloadList) getYearlyBalance(props.subjectId, selectedYear, searchObject, true, false)
      })
      .catch((errors) => PopupError({ text: labels.GENERIC_ERROR }))
  }

  const _setDropdownYears = () => {
    const initialYear = 2011 // anno di inizio comunicazione saldi come da normativa
    let currentYear = new Date().getFullYear()
    //vengono mostrati tutti gli anni dal 2011 fino a quello corrente
    let years = []
    for (let i = currentYear; i >= initialYear; i--) {
      years.push({ id: i, description: `Anno: ${i}` })
    }
    setDropdownYears(years)
  }

  const handlerYearChange = (value) => {
    setSelectedYear(parseInt(value))
    getYearlyBalance(props.subjectId, parseInt(value), searchObject, true)
  }

  const getRelationshipToUpdate = (relationships = [], relationship) => {
    let index = relationships.findIndex((rel) => rel.id === relationship?.id)
    if (index === -1) {
      relationships.push(relationship)
      index = relationships.findIndex((rel) => rel.id === relationship?.id)
    }

    if (relationships[index].coraRelationBalances === undefined) {
      relationships[index].coraRelationBalances = []
    }

    if (relationships[index].coraRelationBalances[0] === undefined) {
      relationships[index].coraRelationBalances[0] = {}
    }
    return index
  }

  //prende un valore dal NumberFormat. contiene valore in formato float, valore formattato in stringa e valore raw
  const handlerAmount = (relationship, field, value) => {
    let newList = cloneDeep(yearlyBalanceRelationships)
    const index = getRelationshipToUpdate(newList, relationship)
    newList[index].coraRelationBalances[0][field] = value.floatValue
    setIsEdit(true)
    setYearlyBalanceRelationships(newList)
  }

  const handlerCurrency = (relationship, value) => {
    let newList = cloneDeep(yearlyBalanceRelationships)
    const index = getRelationshipToUpdate(newList, relationship)
    setIsEdit(true)
    newList[index].coraRelationBalances[0].currency = {
      id: value.id?.toString().trim(),
      isoCode: value.isoCode,
      description: value.description
    }
    setYearlyBalanceRelationships(newList)
  }

  const handlerCurrencyType = (relationship, value) => {
    let newList = cloneDeep(yearlyBalanceRelationships)
    const index = getRelationshipToUpdate(newList, relationship)
    if (Utility.isNotEmpty(value)) {
      newList[index].coraRelationBalances[0].currencyType = {
        id: value.id
      }
    } else {
      newList[index].coraRelationBalances[0].currencyType = null
    }
    setIsEdit(true)
    setYearlyBalanceRelationships(newList)
  }

  const handlerIdResult = (relationship, e) => {
    let newList = cloneDeep(yearlyBalanceRelationships)
    const index = getRelationshipToUpdate(yearlyBalanceRelationships, relationship)
    newList[index].coraRelationBalances[0].idResult = e.target.value.toUpperCase().trim()
    setIsEdit(true)
    setYearlyBalanceRelationships(newList)
  }

  const handlerDontSendCommunication = () => {
    let selectedToExclude = cloneDeep(excludedItems)
    let newList = cloneDeep(yearlyBalanceRelationships)
    for (const checkedItem of Object.keys(checkedItems)) {
      if (checkedItem) {
        let idToRemove = parseInt(checkedItem)
        selectedToExclude.push(idToRemove)
        let index = newList.map((item) => item.id).indexOf(idToRemove)
        newList.splice(index, 1)
      }
    }
    setExcludedItems(selectedToExclude)
    setYearlyBalanceRelationships(newList)
    setCheckedItems([])
  }

  const getDisplayedAmount = (balance, amount) => {
    if (Utility.isEmpty(balance?.id) && Utility.isEmpty(amount)) {
      return ''
    } else if (Utility.isNotEmpty(balance?.id) && Utility.isEmpty(amount)) {
      return '0'
    } else {
      return amount
    }
  }

  const getCurrencies = () => {
    if (currencies.length > 0) {
      const _currencies = currencies.map((currency) => {
        return {
          id: currency.id,
          description: currency.isoCode
        }
      })
      _currencies.sort((a, b) =>
        a.description.localeCompare(b.description, 'en', { sensitivity: 'base' })
      )
      return _currencies
    }
    return []
  }

  const convertToNumber = (_newSearchObject, field) => {
    if (_newSearchObject[field] && _newSearchObject[field] !== '') {
      _newSearchObject[field] = parseFloat(_newSearchObject[field])
    }
    return _newSearchObject
  }

  const setNewSearchObject = (_newSearchObject) => {
    _newSearchObject = convertToNumber(_newSearchObject, 'amount1')
    _newSearchObject = convertToNumber(_newSearchObject, 'amount2')
    _newSearchObject = convertToNumber(_newSearchObject, 'amount3')
    _newSearchObject = convertToNumber(_newSearchObject, 'amount4')
    _newSearchObject = convertToNumber(_newSearchObject, 'amount5')
    setSearchObject(_newSearchObject)
  }

  const renderNumberCell = (
    value,
    valueName,
    relationship,
    className,
    disabled,
    rules,
    handler
  ) => {
    return (
      <NumberCell
        value={value}
        valueName={valueName}
        relationship={relationship}
        className={className}
        disabled={disabled}
        rules={rules}
        handler={handler}
      />
    )
  }

  const header = [
    TableUtils.composeHeader({
      fieldName: 'completeName',
      orderingName: 'completeName',
      displayedName: labels.REGISTRY,
      maxWidth: '1.7fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'relationshipType',
      displayedName: labels.RELATIONSHIP_TYPE,
      maxWidth: '1.3fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'startDate',
      type: fieldTypes.DATERANGE,
      displayedName: labels.START_DATE,
      maxWidth: '1fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'endDate',
      type: fieldTypes.DATERANGE,
      displayedName: labels.END_DATE,
      maxWidth: '1fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'amount1',
      displayedName: labels.AMOUNT_1,
      maxWidth: '1fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'amount2',
      displayedName: labels.AMOUNT_2,
      maxWidth: '1fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'amount3',
      displayedName: labels.AMOUNT_3,
      maxWidth: '1fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'amount4',
      displayedName: labels.AMOUNT_4,
      maxWidth: '1fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'amount5',
      component: (sort, searchObject, h, defaultSort) => {
        return (
          <div onClick={(e) => sort(searchObject, h, defaultSort, e)}>
            <StarTooltip text={labels.OTHER_INFO_AMOUNT_5}>
              <span>{labels.AMOUNT_5}</span>
            </StarTooltip>
          </div>
        )
      },
      maxWidth: '1fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'stock',
      displayedName: labels.AVERAGE_STOCK,
      maxWidth: '1fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'currencies',
      displayedName: labels.CURRENCY,
      type: fieldTypes.SELECTION,
      options: getCurrencies(),
      maxWidth: '1fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'currencyTypes',
      displayedName: labels.NATURE,
      type: fieldTypes.SELECTION,
      options: currencyTypes,
      maxWidth: '2fr'
    }),
    TableUtils.composeHeader({
      fieldName: 'idResult',
      displayedName: labels.RESULT,
      maxWidth: '1fr'
    })
  ]

  const prepareRows = (newYearRelations = []) => {
    const list = Array.isArray(newYearRelations) ? newYearRelations : []
    let smallcolclass = 'text-truncate'
    let smallcolClassNumber = `${smallcolclass} text-end`
    return list.map((c) => {
      return TableUtils.composeRow({
        id: c.id,
        rowData: c,
        cellData: {
          completeName: TableUtils.composeCell({
            fieldName: 'completeName',
            additionalClass: props.subject?.archived ? 'archived' : '',
            fieldValue: c.completeName
          }),
          relationshipType: TableUtils.composeCell({
            fieldName: 'relationshipType',
            additionalClass: props.subject?.archived ? 'archived' : '',
            fieldValue: c.relationshipType?.code + ' - ' + c.relationshipType?.description
          }),
          startDate: TableUtils.composeCell({
            fieldName: 'startDate',
            additionalClass: props.subject?.archived ? 'archived' : '',
            fieldValue: c.startDate && Utility.formatDateForDisplay(c.startDate)
          }),
          endDate: TableUtils.composeCell({
            fieldName: 'endDate',
            additionalClass: props.subject?.archived ? 'archived' : '',
            fieldValue: c.endDate && Utility.formatDateForDisplay(c.endDate)
          }),
          amount1: TableUtils.composeCell({
            fieldName: 'amount1',
            additionalClass: props.subject?.archived ? 'archived' : '',
            component: () => {
              let balance = c?.coraRelationBalances[0]
              const rules = getBalanceRulesByRelationshipType(c?.relationshipType?.code)
              const disabledFields = getDisabledFields(rules)
              return renderNumberCell(
                getDisplayedAmount(balance, balance?.amount1),
                'amount1',
                c,
                smallcolClassNumber,
                disabledFields.amount1Disabled || props.subject?.archived,
                rules,
                handlerAmount
              )
            }
          }),
          amount2: TableUtils.composeCell({
            fieldName: 'amount2',
            additionalClass: props.subject?.archived ? 'archived' : '',
            component: () => {
              let balance = c?.coraRelationBalances[0]
              const rules = getBalanceRulesByRelationshipType(c?.relationshipType?.code)
              const disabledFields = getDisabledFields(rules)
              return renderNumberCell(
                getDisplayedAmount(balance, balance?.amount2),
                'amount2',
                c,
                smallcolClassNumber,
                disabledFields.amount2Disabled || props.subject?.archived,
                rules,
                handlerAmount
              )
            }
          }),
          amount3: TableUtils.composeCell({
            fieldName: 'amount3',
            additionalClass: props.subject?.archived ? 'archived' : '',
            component: () => {
              let balance = c?.coraRelationBalances[0]
              const rules = getBalanceRulesByRelationshipType(c?.relationshipType?.code)
              const disabledFields = getDisabledFields(rules)
              return renderNumberCell(
                getDisplayedAmount(balance, balance?.amount3),
                'amount3',
                c,
                smallcolClassNumber,
                disabledFields.amount3Disabled || props.subject?.archived,
                rules,
                handlerAmount
              )
            }
          }),
          amount4: TableUtils.composeCell({
            fieldName: 'amount4',
            additionalClass: props.subject?.archived ? 'archived' : '',
            component: () => {
              let balance = c?.coraRelationBalances[0]
              const rules = getBalanceRulesByRelationshipType(c?.relationshipType?.code)
              const disabledFields = getDisabledFields(rules)
              return renderNumberCell(
                getDisplayedAmount(balance, balance?.amount4),
                'amount4',
                c,
                smallcolClassNumber,
                disabledFields.amount4Disabled || props.subject?.archived,
                rules,
                handlerAmount
              )
            }
          }),
          amount5: TableUtils.composeCell({
            fieldName: 'amount5',
            additionalClass: props.subject?.archived ? 'archived' : '',
            component: () => {
              let balance = c?.coraRelationBalances[0]
              const rules = getBalanceRulesByRelationshipType(c?.relationshipType?.code)
              const disabledFields = getDisabledFields(rules)
              return renderNumberCell(
                getDisplayedAmount(balance, balance?.amount5),
                'amount5',
                c,
                smallcolClassNumber,
                disabledFields.amount5Disabled || props.subject?.archived,
                rules,
                handlerAmount
              )
            }
          }),
          stock: TableUtils.composeCell({
            fieldName: 'stock',
            additionalClass: props.subject?.archived ? 'archived' : '',
            component: () => {
              let balance = c?.coraRelationBalances[0]
              const rules = getBalanceRulesByRelationshipType(c?.relationshipType?.code)
              const disabledFields = getDisabledFields(rules)
              return renderNumberCell(
                getDisplayedAmount(balance, balance?.stock),
                'stock',
                c,
                smallcolClassNumber,
                disabledFields.stockDisabled || props.subject?.archived,
                rules,
                handlerAmount
              )
            }
          }),
          currencies: TableUtils.composeCell({
            fieldName: 'currencies',
            additionalClass: props.subject?.archived ? 'archived' : '',
            component: () => {
              let balance = c?.coraRelationBalances[0]
              return (
                <BwmSelect
                  disabled={props.subject?.archived}
                  options={currencies}
                  name={`currency_${c.id}`}
                  optionValue="id"
                  optionLabel="isoCode"
                  isClearable={false}
                  isCell={true}
                  value={balance?.currency?.id || 58}
                  onChange={(value) => handlerCurrency(c, value)}
                />
              )
            }
          }),
          currencyTypes: TableUtils.composeCell({
            fieldName: 'currencyTypes',
            additionalClass: props.subject?.archived ? 'archived' : '',
            component: () => {
              let balance = c?.coraRelationBalances[0]
              return (
                <BwmSelect
                  disabled={props.subject?.archived}
                  options={currencyTypes}
                  name={`currencyType_${c.id}`}
                  optionValue="id"
                  optionCode="code"
                  optionLabel="description"
                  isCell={true}
                  value={balance?.currencyType?.id || null}
                  onChange={(value) => handlerCurrencyType(c, value)}
                />
              )
            }
          }),
          idResult: TableUtils.composeCell({
            fieldName: 'idResult',
            additionalClass: props.subject?.archived ? 'archived' : '',
            component: () => {
              let balance = c?.coraRelationBalances[0]
              return (
                <BwmInput
                  disabled={props.subject?.archived}
                  name={'idResult'}
                  className={'form-control form-control-cell'}
                  value={balance?.idResult || ''}
                  onChange={(e) => handlerIdResult(c, e)}
                />
              )
            }
          })
        }
      })
    })
  }

  const cancelButton = () => {
    return (
      <button
        type="button"
        className="btn btn-outline-primary me-4"
        onClick={() => getYearlyBalance(props.subjectId, selectedYear, searchObject, true, false)}>
        <i className="star-icon-cancel star-icon me-2" />
        {labels.CANCEL}
      </button>
    )
  }

  const saveButton = () => {
    return (
      <button
        disabled={props.subject?.archived}
        className="btn btn-primary me-2 mb-1"
        onClick={saveBalances}>
        <i className="star-icon-floppy-disk star-icon me-2" />
        {labels.SAVE}
      </button>
    )
  }

  return (
    <>
      <div className="row mb-2">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex" role="group" aria-label="action buttons">
              <div className="pe-2">
                <BtnFilter
                  isClose={hideSearch}
                  label={labels.SEARCH}
                  resetLabel={labels.RESET}
                  handlerOnClick={() => setHideSearch(!hideSearch)}
                />
              </div>
              <div className="pe-2">
                <Dropdown onSelect={(eventKey) => handlerYearChange(eventKey)}>
                  <DropdownButton
                    title={`${labels.YEAR}: ${selectedYear}`}
                    bsPrefix={'dropdown-menu-green btn-fill m-0 me-1 pe-2'}>
                    {dropdownYears &&
                      dropdownYears.map((el, index) => {
                        return (
                          <Dropdown.Item
                            key={index}
                            eventKey={el.id}
                            active={el.id === selectedYear}>
                            {el.description}
                          </Dropdown.Item>
                        )
                      })}
                  </DropdownButton>
                </Dropdown>
              </div>
              <div className="pe-2">
                <StarTooltip text={labels.BALANCES_INSTRUCTIONS}>
                  <button className="btn btn-outline-primary" onClick={() => openGuideBalances()}>
                    <i className="star-icon-question star-icon" />
                    {window.innerWidth > Constants.BREAKPOINT_LG && (
                      <span className="ms-2">{labels.BALANCES_INSTRUCTIONS}</span>
                    )}
                  </button>
                </StarTooltip>
              </div>
              <div className="pe-2">
                <YearlyBalanceExportModal
                  disabled={props.subject?.archived}
                  subject={subject}
                  selectedYear={selectedYear}
                  subjectId={props.subjectId}
                />
              </div>
              <div className="pe-2">
                <YearlyBalanceImportModal
                  disabled={props.subject?.archived}
                  subjectId={props.subjectId}
                  subject={subject}
                  selectedYear={selectedYear}
                  getYearlyBalance={() =>
                    getYearlyBalance(props.subjectId, selectedYear, searchObject, true)
                  }
                />
              </div>
              <div>
                <StarTooltip text={labels.EXCLUDE_FROM_COMMUNICATION}>
                  <button
                    disabled={Object.keys(checkedItems)?.length === 0 || props.subject?.archived}
                    type="button"
                    className={`btn btn-outline-primary ${(Object.keys(checkedItems)?.length === 0 || props.subject?.archived) && 'disabled'}`}
                    onClick={handlerDontSendCommunication}>
                    <i className="star-icon-exclude star-icon" />
                    {window.innerWidth > Constants.BREAKPOINT_LG && (
                      <span className="ms-2">{labels.EXCLUDE_FROM_COMMUNICATION}</span>
                    )}
                  </button>
                </StarTooltip>
              </div>
            </div>
            <div className="d-flex justify-content-end" role="group" aria-label="action buttons">
              <span className="ms-2">
                <CreateCommunicationModal
                  type="YEARLY"
                  subjectId={props.subjectId}
                  subject={subject}
                  selectedYear={selectedYear}
                  excludedRelationships={excludedItems}
                  disabled={yearlyBalanceRelationships?.length === 0 || props.subject?.archived}
                  onClickCreateCommunicationBefore={saveBalances}
                />
              </span>
            </div>
          </div>
        </div>
      </div>

      <StarTable
        version="1.1"
        loading={loading}
        headerColumns={header}
        hideSearch={hideSearch}
        checkedItems={checkedItems}
        rows={rows}
        offset={260}
        tableContainerWidth={getTableContainerWidth()}
        tableConfigurationKey={tableName}
        searchObjectPrototype={searchObject}
        setSearchObject={setNewSearchObject}
        onExecutedSearch={(content) => setYearlyBalanceRelationships(content)}
        searchCallBack={(_searchObject) =>
          getYearlyBalance(props.subjectId, selectedYear, _searchObject)
        }
        extraComponentBottomLeft={cancelButton()}
        actionsComponent={saveButton()}
        actionsComponentOnTop={false}
        usePagination={true}
        withCheckBoxes={true}
        onHideSearch={(val) => setHideSearch(val)}
        onClickCheck={setNewCheckedItems}
      />
    </>
  )
}
